import { useCallback, useState } from "react";
import type { SiteCreatePayload } from "@/customTypings/site/Site";
import type { SiteDeletedRestoreAvailableConflict } from "@/utils/site/siteCreateConflict";
import {
    SITE_CREATE_CONFLICT,
    SiteCreateConflictError,
    isSiteDeletedRestoreAvailableConflict,
} from "@/utils/site/siteCreateConflict";
import { DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, showToastError, showToastSuccess } from "@/utils";

type UseSiteRestoreOnCreateConflictOptions<TDetail extends { id: string }> = {
    restoreDeletedWithCreateValues: (payload: SiteCreatePayload & { id: string }) => Promise<TDetail>;
    onRestored: (detail: TDetail, siteName: string) => void;
};

export function useSiteRestoreOnCreateConflict<TDetail extends { id: string }>({
    restoreDeletedWithCreateValues,
    onRestored,
}: UseSiteRestoreOnCreateConflictOptions<TDetail>) {
    const [restoreConflict, setRestoreConflict] =
        useState<SiteDeletedRestoreAvailableConflict | null>(null);
    const [pendingRestoreForm, setPendingRestoreForm] = useState<SiteCreatePayload | null>(null);
    const [restoringDeleted, setRestoringDeleted] = useState(false);

    const handleCreateError = useCallback((err: unknown): boolean => {
        if (err instanceof SiteCreateConflictError) {
            if (
                isSiteDeletedRestoreAvailableConflict(
                    err.conflict,
                    SITE_CREATE_CONFLICT.deletedRestoreAvailable
                )
            ) {
                setRestoreConflict(err.conflict);
                return true;
            }
        }
        return false;
    }, []);

    const clearRestoreConflict = useCallback(() => {
        setRestoreConflict(null);
        setPendingRestoreForm(null);
    }, []);

    const confirmRestore = useCallback(
        async (confirmed: boolean) => {
            if (!confirmed || !restoreConflict || !pendingRestoreForm) {
                clearRestoreConflict();
                return;
            }
            setRestoringDeleted(true);
            try {
                const restored = await restoreDeletedWithCreateValues({
                    id: restoreConflict.existingId,
                    ...pendingRestoreForm,
                });
                const siteName = restoreConflict.existingSiteName;
                clearRestoreConflict();
                showToastSuccess(`"${siteName}" restored successfully!`);
                onRestored(restored, siteName);
            } catch {
                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
            } finally {
                setRestoringDeleted(false);
            }
        },
        [restoreConflict, pendingRestoreForm, restoreDeletedWithCreateValues, onRestored, clearRestoreConflict]
    );

    const restoreModalProps = {
        show: restoreConflict !== null && pendingRestoreForm !== null,
        title: "Restore deleted site?",
        body: restoreConflict
            ? "A deleted Site matching these details already exists. Do you want to restore it using the details you entered?"
            : "",
        buttonSuccessText: restoringDeleted ? "Restoring…" : "Yes",
        buttonCancelText: "No" as const,
    };

    return {
        handleCreateError,
        setPendingRestoreForm,
        confirmRestore,
        restoreModalProps,
    };
}
